/**
 * Invoice Management Page
 * Main page with tabs for all invoices, create invoice, and invoice detail views.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, RefreshCw, DollarSign, Clock, AlertTriangle,
  CheckCircle, Receipt, Settings
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { InvoiceList } from './InvoiceList';
import { CreateEditInvoice } from './CreateEditInvoice';
import { InvoiceDetail } from './InvoiceDetail';
import { InvoiceTemplateSettings } from './InvoiceTemplateSettings';
import { RevOpsProvider } from '@/contexts/RevOpsContext';
import './InvoicePrintStyles.css';
import {
  invoiceAPI,
  Invoice,
  InvoiceStats,
  InvoiceFilters,
  formatCents,
} from '@/services/api/invoiceAPI';
import { config as productConfig } from '@/src/config';
import { useTabbedChrome } from '@/components/weavein';

interface InvoiceManagementProps {
  organizationId: number;
  organizationName: string;
}

export function InvoiceManagement({ organizationId, organizationName }: InvoiceManagementProps) {
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState('list');
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [byOrg, setByOrg] = useState<import('@/services/api/invoiceAPI').InvoiceOrgBreakdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [editInvoiceId, setEditInvoiceId] = useState<number | null>(null);
  const [duplicateFromId, setDuplicateFromId] = useState<number | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const result = await invoiceAPI.listInvoices(organizationId, { page: 1, page_size: 1 });
      setStats(result.stats);
      setByOrg(result.by_org || []);
    } catch (err) {
      console.error('Failed to fetch invoice stats:', err);
    }
  }, [organizationId]);

  useEffect(() => {
    setLoading(true);
    fetchStats().finally(() => setLoading(false));
  }, [fetchStats]);

  // In the Trust Care reference view the list aggregates other orgs' invoices,
  // so detail/edit must target the invoice's OWN cbo, not this page's org.
  const [rowCbo, setRowCbo] = useState<number | null>(null);
  const targetCbo = rowCbo ?? organizationId;

  const handleViewInvoice = (invoiceId: number, cboId?: number) => {
    setRowCbo(cboId ?? null);
    setSelectedInvoiceId(invoiceId);
    setActiveTab('detail');
  };

  const handleEditInvoice = (invoiceId: number, cboId?: number) => {
    if (cboId !== undefined) setRowCbo(cboId);
    setEditInvoiceId(invoiceId);
    setActiveTab('create');
  };

  const handleCreateNew = () => {
    setEditInvoiceId(null);
    setDuplicateFromId(null);
    setActiveTab('create');
  };

  const handleDuplicateInvoice = (invoiceId: number) => {
    setEditInvoiceId(null);
    setDuplicateFromId(invoiceId);
    setActiveTab('create');
  };

  const handleBack = () => {
    setSelectedInvoiceId(null);
    setEditInvoiceId(null);
    setDuplicateFromId(null);
    setActiveTab('list');
    fetchStats();
  };

  const handleInvoiceCreated = (invoice: Invoice) => {
    setSelectedInvoiceId(invoice.id);
    setActiveTab('detail');
    fetchStats();
  };

  return (
    <div className={`max-w-7xl mx-auto p-6 space-y-6${productConfig.isTriveny ? ' triveny-platform' : ''}`}>
      {/* Header — title hidden for Triveny (TvPillarBar shows "OPERATE · Invoices"); actions kept. */}
      <div className="flex items-center justify-between">
        {!productConfig.isTriveny && (
          <div className="flex items-center gap-3">
            {!tabbed && <Receipt className="w-7 h-7 text-blue-600 dark:text-blue-400" />}
            <div>
              {!tabbed && (
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Invoices
              </h1>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Create, send, and manage invoices for {organizationName}
              </p>
            </div>
          </div>
        )}
        {activeTab === 'list' && (
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setActiveTab('templates')}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              <Settings className="w-4 h-4" />
              Templates
            </button>
            <button
              onClick={handleCreateNew}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              New Invoice
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && activeTab === 'list' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium mb-1">
              <FileText className="w-3.5 h-3.5" />
              DRAFTS
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.draft_count}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-blue-500 text-xs font-medium mb-1">
              <Clock className="w-3.5 h-3.5" />
              OUTSTANDING
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCents(stats.total_outstanding_cents)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{stats.outstanding_count} invoices</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-red-500 text-xs font-medium mb-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              OVERDUE
            </div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.overdue_count}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-green-500 text-xs font-medium mb-1">
              <DollarSign className="w-3.5 h-3.5" />
              PAID THIS MONTH
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCents(stats.paid_this_month_cents)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{stats.paid_this_month} invoices</p>
          </div>
        </div>
      )}

      {/* Per-client revenue breakdown (reference org / Trust Care only) */}
      {activeTab === 'list' && byOrg.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
            Revenue by organization
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Organization</th>
                  <th className="text-right px-4 py-2 font-medium">Invoices</th>
                  <th className="text-right px-4 py-2 font-medium">Total billed</th>
                  <th className="text-right px-4 py-2 font-medium">Outstanding</th>
                  <th className="text-right px-4 py-2 font-medium">Paid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {byOrg.map((o) => (
                  <tr key={o.cbo_id}>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                      {o.org_name}
                      {o.draft_count > 0 && (
                        <span className="ml-2 rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-xs text-gray-500">{o.draft_count} draft</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{o.invoice_count}</td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCents(o.total_cents)}</td>
                    <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{formatCents(o.outstanding_cents)}</td>
                    <td className="px-4 py-2 text-right text-green-600 dark:text-green-400">{formatCents(o.paid_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Content */}
      {activeTab === 'list' && (
        <InvoiceList
          organizationId={organizationId}
          onView={handleViewInvoice}
          onEdit={handleEditInvoice}
          onCreate={handleCreateNew}
          onRefresh={fetchStats}
        />
      )}

      {activeTab === 'create' && (
        <RevOpsProvider>
          <CreateEditInvoice
            organizationId={editInvoiceId ? targetCbo : organizationId}
            organizationName={organizationName}
            invoiceId={editInvoiceId}
            duplicateFromId={duplicateFromId}
            onBack={handleBack}
            onCreated={handleInvoiceCreated}
          />
        </RevOpsProvider>
      )}

      {activeTab === 'detail' && selectedInvoiceId && (
        <RevOpsProvider>
          <InvoiceDetail
            organizationId={targetCbo}
            organizationName={organizationName}
            invoiceId={selectedInvoiceId}
            onBack={handleBack}
            onEdit={handleEditInvoice}
            onDuplicate={handleDuplicateInvoice}
          />
        </RevOpsProvider>
      )}

      {activeTab === 'templates' && (
        <div>
          <button
            onClick={handleBack}
            className="mb-4 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            &larr; Back to Invoices
          </button>
          <RevOpsProvider>
            <InvoiceTemplateSettings />
          </RevOpsProvider>
        </div>
      )}
    </div>
  );
}
